/**
 * [sc-48351] Le regole dei campi di un articolo che cambiano con la categoria.
 *
 * Stavano scritte due volte, nella pagina di creazione di Listini e nella
 * modale che crea un articolo dal preventivo, e la modale era rimasta
 * indietro: niente sanitari, rubinetteria, mobili... Ora le usano tutte e due
 * da qui, insieme ai campi di `ItemCategoryFields.vue`.
 */
import {
  ITEM_CATEGORY_VALUES,
  SALES_UNITS,
  itemCategoriesFor,
} from "@/config/catalog";

/** Quali campi mostrare per ogni valore di `items.category`. */
export const COLUMN_GROUPS = Object.freeze({
  area: Object.freeze([
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ]),
  piece: Object.freeze([
    "sanitari",
    "rubinetteria",
    "box doccia",
    "accessori bagno",
  ]),
  linear: Object.freeze(["profili", "sistemi di scarico"]),
  furniture: Object.freeze(["mobili", "porte e infissi"]),
  bath: Object.freeze(["bagno"]),
});

export function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

/** Le tipologie delle piastrelle, obbligatorie prima degli altri campi. */
export const TILE_SUBCATEGORIES = Object.freeze([
  "Elementi Lineari",
  "Decori",
  "Fondi",
  "Battiscopa",
  "Listelli",
  "Gradini",
  "Scalini",
  "Altri Pezzi Speciali",
]);

/** Il gruppo dei campi che segue ogni unità di vendita. */
const GRUPPO_DELL_UNITA = Object.freeze({
  ML: "linear",
  M2: "area",
  PZ: "piece",
});

/**
 * Il gruppo di campi di una piastrella.
 *
 * [sc-48951] Comanda l'unità di vendita, la tipologia solo se l'unità non
 * dice niente (non ancora scelta, o confezione, scatola...). Prima comandava
 * la tipologia: «Elementi Lineari» venduto a Metro quadro mostrava
 * «ML / scatola», mentre il preventivo, che fattura secondo l'unità,
 * pretendeva «M² / scatola». Il modulo chiedeva una cosa, il preventivo
 * un'altra, e la riga restava gialla per sempre.
 */
export function tileGroupOf(category, subCategory, salesUnit = "") {
  if (category !== "piastrelle") return null;
  const dallUnita = GRUPPO_DELL_UNITA[String(salesUnit || "").toUpperCase()];
  if (dallUnita) return dallUnita;
  if (subCategory === "Elementi Lineari") return "linear";
  if (subCategory === "Decori" || subCategory === "Fondi") return "area";
  if (TILE_SUBCATEGORIES.slice(3).includes(subCategory)) return "piece";
  return null;
}

/**
 * L'unità con cui si vende di solito una tipologia. Solo quelle che ne hanno
 * una sola: un battiscopa si vende a pezzo o a metro lineare, e tutte e due
 * vanno bene.
 */
export const UNITA_DELLA_TIPOLOGIA = Object.freeze({
  "Elementi Lineari": "ML",
  Decori: "M2",
  Fondi: "M2",
});

/**
 * [sc-48951] Tipologia e unità di vendita si contraddicono?
 *
 * Restituisce `null` se vanno d'accordo (o se non c'è ancora niente da
 * confrontare), altrimenti cosa dire: l'unità attesa e se l'azienda la vende.
 * Il modulo mostra l'avviso e un bottone per passare all'unità attesa. Non la
 * cambia da solo: l'unità la sceglie l'operatore (sc-48350 / task 48396).
 *
 * @param {{category: string, subCategory: string, salesUnit: string,
 *          opzioni: {value: string, label: string}[]}} p
 */
export function avvisoUnitaTipologia({
  category,
  subCategory,
  salesUnit,
  opzioni = [],
}) {
  if (category !== "piastrelle" || !salesUnit) return null;
  const attesa = UNITA_DELLA_TIPOLOGIA[subCategory];
  if (!attesa || attesa === salesUnit) return null;
  const etichetta = (valore) =>
    SALES_UNITS.find((u) => u.value === valore)?.label ?? valore;
  return {
    tipologia: subCategory,
    attesa,
    etichettaAttesa: etichetta(attesa),
    etichettaScelta: etichetta(salesUnit),
    disponibile: opzioni.some((u) => u.value === attesa),
  };
}

/** Campi per scatola che il blocco della categoria mostra già. */
export function boxFieldsFromCategory(category) {
  const group = getCategoryGroup(category);
  if (group === "area") {
    return category === "piastrelle"
      ? ["sqmPerBox", "mlPerBox", "piecesPerBox"]
      : ["sqmPerBox", "piecesPerBox"];
  }
  if (group === "linear") return ["mlPerBox"];
  return [];
}

/** [sc-48350 / task 48396] Segue l'unità scelta, non la categoria. */
export function priceLabelFor(salesUnit) {
  if (salesUnit === "ML") return "Prezzo / ML";
  if (salesUnit === "M2") return "Prezzo / M²";
  if (salesUnit === "PZ") return "Prezzo / pz";
  return "Prezzo";
}

/** «rivestimenti parete» in tendina si legge meglio con la maiuscola. */
export function etichettaCategoria(valore) {
  const testo = String(valore || "");
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

/**
 * Le categorie in cui la modale del preventivo può creare un articolo: quelle
 * delle schede scelte in configurazione, come in Listini. Chi non ha ancora
 * scelto le vede tutte. `bagno` è un valore vecchio: non si crea più.
 *
 * @param {string[]} [chosen] macro da `companies.productCategories`
 * @returns {string[]} valori di `items.category`, nell'ordine del catalogo
 */
export function creationCategoriesFor(chosen) {
  const scelte =
    Array.isArray(chosen) && chosen.length > 0
      ? itemCategoriesFor(chosen)
      : ITEM_CATEGORY_VALUES;
  return ITEM_CATEGORY_VALUES.filter(
    (categoria) => categoria !== "bagno" && scelte.includes(categoria)
  );
}
